import auth from '@/modules/auth/authController.js';
import { createLogger } from '@utils/logger.js';

/**
 * ApiService handles API calls with optional authorization,
 * data payloads, file uploads with progress tracking, and support for JSON or Blob responses.
 * @class ApiService
 */
class ApiService {
    /**
     * Initializes a new instance or returns the existing singleton instance.
     * @constructor
     */
    constructor() {
        if (ApiService.instance) return ApiService.instance;
        ApiService.instance = this;

        this.logger = createLogger('API');
        this.refreshInProgress = null; // Track ongoing refresh Promise
    }

    /**
     * Makes an API call with options for authorization, 
     * file uploads, and different response types.
     * @param {string} endpoint - The API endpoint URL.
     * @param {string} [method="POST"] - The HTTP method to use.
     * @param {Object|FormData|null} [data=null] - The request payload.
     * @param {boolean} [isFileUpload=false] - Whether the call is a file upload.
     * @param {boolean} [isBlob=false] - Whether the response should be a Blob.
     * @returns {Promise<Object|Blob>} - Parsed JSON or Blob data.
     * @throws {Object} - An error object containing status and message.
     */
    async apiCall(endpoint, method = "POST", data = null, isFileUpload = false, isBlob = false) {
        try {
            const options = { method, headers: {} };

            // Add Authorization header if user is authenticated
            const isAuthenticated = await auth.isAuthenticated();
            this.logger.info(`API Call - Authenticated: ${isAuthenticated}`);

            if (isAuthenticated) {
                // ACTIVITY-BASED TOKEN REFRESH:
                // Refresh token on every API call if it has less than 30 minutes remaining
                // This creates a "sliding window" effect where activity extends the session
                await this.refreshTokenIfNeeded();

                const token = await auth.getAccessToken();
                this.logger.info(`API Call - Token: ${token ? 'Present' : 'Missing'}`);
                options.headers["Authorization"] = `Bearer ${token}`;
            }

            // Set request body and headers based on content type
            if (isFileUpload) {
                options.body = data; // FormData handles Content-Type automatically
            } else if (data) {
                options.body = JSON.stringify(data);
                options.headers["Content-Type"] = "application/json";
            }

            // Perform the fetch request
            const response = await fetch(endpoint, options);

            // Get the response data
            const responseData = isBlob ? await response.blob() : await response.json();

            // Throw detailed error if response is not OK
            if (!response.ok) {
                throw {
                    status: response.status,
                    message: responseData.error || "Unknown error occurred",
                    data: responseData
                };
            }

            // Return both status and data
            return {
                status: response.status,
                ok: response.ok,
                data: responseData,
                ...responseData // Spread the JSON data for backward compatibility
            };

        } catch (error) {
            this.logger.error("API call error:", error);
            throw error;
        }
    }

    /**
     * Refresh token if it has less than 5 minutes remaining
     * This implements "sliding window" session timeout based on activity
     * Includes request deduplication to prevent concurrent refresh calls
     * @private
     */
    async refreshTokenIfNeeded() {
        try {
            // Get token expiry from localStorage
            const expiresAt = localStorage.getItem('admin_expiresAt');
            if (!expiresAt) return;

            const timeRemaining = parseInt(expiresAt) - Date.now();
            const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes

            // If less than 5 minutes remaining, refresh the token
            // This ensures any activity extends the session before token expires
            if (timeRemaining < fiveMinutesInMs && timeRemaining > 0) {

                // REQUEST DEDUPLICATION: If a refresh is already in progress, wait for it
                if (this.refreshInProgress) {
                    this.logger.debug('Token refresh already in progress, waiting...');
                    await this.refreshInProgress;
                    return;
                }

                this.logger.debug('Token has less than 5 minutes remaining, refreshing...');

                // Get token manager from auth controller
                const tokenManager = auth.tokenManager;
                if (tokenManager && typeof tokenManager.refreshToken === 'function') {
                    // Create the refresh Promise and store it
                    this.refreshInProgress = tokenManager.refreshToken()
                        .then(() => {
                            this.logger.success('Token refreshed due to activity');
                        })
                        .finally(() => {
                            // Clear the flag when done (success or failure)
                            this.refreshInProgress = null;
                        });

                    // Wait for the refresh to complete
                    await this.refreshInProgress;
                }
            }
        } catch (error) {
            // Don't throw - let the main request continue with existing token
            this.logger.warn('Token refresh check failed:', error);
            // Make sure to clear the flag on error
            this.refreshInProgress = null;
        }
    }

    /**
     * Upload a single file with progress tracking
     * @param {string} endpoint - The API endpoint URL for file upload
     * @param {File} file - The file to upload
     * @param {Object} [additionalData={}] - Additional data to send with the file
     * @param {Function} [onProgress=null] - Progress callback function (receives percentage)
     * @param {Object} [options={}] - Upload options
     * @returns {Promise<Object>} - Upload response
     */
    async uploadFile(endpoint, file, additionalData = {}, onProgress = null, options = {}) {
        const {
            timeout = 300000, // 5 minutes default timeout
            fieldName = 'file' // Default field name for the file
        } = options;

        return new Promise(async (resolve, reject) => {
            try {
                const xhr = new XMLHttpRequest();

                // Create FormData
                const formData = new FormData();
                formData.append(fieldName, file);

                this.logger.debug('File upload:', {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    fieldName: fieldName
                });

                // Add additional data
                Object.keys(additionalData).forEach(key => {
                    if (typeof additionalData[key] === 'object') {
                        formData.append(key, JSON.stringify(additionalData[key]));
                    } else {
                        formData.append(key, additionalData[key]);
                    }
                });

                // Progress tracking
                if (onProgress && xhr.upload) {
                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            const percentComplete = Math.round((e.loaded / e.total) * 100);
                            onProgress(percentComplete);
                        }
                    });
                }

                // Success handler
                xhr.addEventListener('load', () => {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({
                                status: xhr.status,
                                ok: true,
                                data: response.data || response,
                                ...response
                            });
                        } else {
                            reject({
                                status: xhr.status,
                                message: response.error || response.message || `HTTP ${xhr.status}: Upload failed`,
                                data: response
                            });
                        }
                    } catch (parseError) {
                        reject({
                            status: xhr.status,
                            message: 'Invalid response format from server',
                            data: xhr.responseText
                        });
                    }
                });

                // Error handlers
                xhr.addEventListener('error', () => {
                    reject({
                        status: 0,
                        message: 'Network error during upload',
                        data: null
                    });
                });

                xhr.addEventListener('timeout', () => {
                    reject({
                        status: 0,
                        message: 'Upload timeout - file may be too large or connection is slow',
                        data: null
                    });
                });

                xhr.addEventListener('abort', () => {
                    reject({
                        status: 0,
                        message: 'Upload was cancelled',
                        data: null
                    });
                });

                // IMPORTANT: Open request BEFORE setting headers
                xhr.open('POST', endpoint);
                xhr.timeout = timeout;

                // Add authentication header AFTER opening the request
                if (await auth.isAuthenticated()) {
                    const token = await auth.getAccessToken();
                    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                }

                // Send the request
                xhr.send(formData);

            } catch (error) {
                reject({
                    status: 0,
                    message: error.message || 'Upload preparation failed',
                    data: error
                });
            }
        });
    }

    /**
     * Upload multiple files with individual progress tracking
     * @param {string} endpoint - The API endpoint URL for file upload
     * @param {File[]} files - Array of files to upload
     * @param {Object} [additionalData={}] - Additional data to send with each file
     * @param {Function} [onFileProgress=null] - Progress callback for individual files (receives file, index, percentage)
     * @param {Function} [onOverallProgress=null] - Overall progress callback (receives percentage)
     * @param {Object} [options={}] - Upload options
     * @returns {Promise<Object[]>} - Array of upload responses
     */
    async uploadFiles(endpoint, files, additionalData = {}, onFileProgress = null, onOverallProgress = null, options = {}) {
        const {
            concurrent = 3, // Number of concurrent uploads
            stopOnError = false // Whether to stop all uploads if one fails
        } = options;

        const results = [];
        const errors = [];
        let completedFiles = 0;

        // Update overall progress
        const updateOverallProgress = () => {
            if (onOverallProgress) {
                const percentage = Math.round((completedFiles / files.length) * 100);
                onOverallProgress(percentage);
            }
        };

        // Upload single file with progress
        const uploadSingleFile = async (file, index) => {
            try {
                const result = await this.uploadFile(
                    endpoint,
                    file,
                    additionalData,
                    (percentage) => {
                        if (onFileProgress) {
                            onFileProgress(file, index, percentage);
                        }
                    },
                    options
                );

                results[index] = result;
                completedFiles++;
                updateOverallProgress();
                return result;
            } catch (error) {
                errors.push({ file, index, error });
                completedFiles++;
                updateOverallProgress();
                
                if (stopOnError) {
                    throw error;
                }
                
                return null;
            }
        };

        // Process files in batches
        const batches = [];
        for (let i = 0; i < files.length; i += concurrent) {
            const batch = files.slice(i, i + concurrent);
            batches.push(batch);
        }

        // Upload batches sequentially, files within batch concurrently
        for (const batch of batches) {
            const batchPromises = batch.map((file, batchIndex) => {
                const globalIndex = batches.indexOf(batch) * concurrent + batchIndex;
                return uploadSingleFile(file, globalIndex);
            });

            if (stopOnError) {
                await Promise.all(batchPromises);
            } else {
                await Promise.allSettled(batchPromises);
            }
        }

        return {
            results: results.filter(result => result !== null),
            errors: errors,
            successful: results.filter(result => result !== null).length,
            failed: errors.length,
            total: files.length
        };
    }

    /**
     * Download a file from the server
     * @param {string} endpoint - The API endpoint URL for file download
     * @param {string} [filename] - Optional filename for the download
     * @returns {Promise<void>}
     */
    async downloadFile(endpoint, filename = null) {
        try {
            const response = await this.apiCall(endpoint, 'GET', null, false, true);
            
            // Create blob URL and trigger download
            const blob = response.data || response;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.style.display = 'none';
            
            if (filename) {
                link.download = filename;
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this.logger.error('Download error:', error);
            throw error;
        }
    }

    /**
     * Stream a file for viewing (opens in new tab)
     * @param {string} endpoint - The API endpoint URL for file viewing
     * @returns {Promise<void>}
     */
    async viewFile(endpoint) {
        try {
            // Fetch the file with authentication
            const response = await this.apiCall(endpoint, 'GET', null, false, true);
            
            // Create blob URL and open in new tab
            const blob = response.data || response;
            const url = window.URL.createObjectURL(blob);
            
            // Open in new tab
            const newWindow = window.open(url, '_blank');
            
            // Clean up URL after a delay
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);
            
            if (!newWindow) {
                throw new Error('Popup blocked. Please allow popups for this site.');
            }
        } catch (error) {
            this.logger.error('View file error:', error);
            throw error;
        }
    }

    /**
     * Validate file before upload
     * @param {File} file - File to validate
     * @param {Object} [constraints={}] - Validation constraints
     * @returns {Object} - Validation result
     */
    validateFile(file, constraints = {}) {
        const {
            maxSize = 50 * 1024 * 1024, // 50MB default
            allowedTypes = [], // Empty array means all types allowed
            allowedExtensions = [], // Empty array means all extensions allowed
            minSize = 0
        } = constraints;

        const errors = [];

        // Size validation
        if (file.size > maxSize) {
            const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            errors.push(`File size (${fileSizeMB}MB) exceeds maximum allowed size (${maxSizeMB}MB)`);
        }

        if (file.size < minSize) {
            errors.push(`File size is too small (minimum: ${minSize} bytes)`);
        }

        // Type validation
        if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
            errors.push(`File type '${file.type}' is not allowed`);
        }

        // Extension validation
        if (allowedExtensions.length > 0) {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
                errors.push(`File extension '${fileExtension}' is not allowed`);
            }
        }

        // Security validation - dangerous extensions
        const dangerousExtensions = ['exe', 'bat', 'com', 'scr', 'vbs', 'js', 'jar', 'cmd', 'ps1'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (dangerousExtensions.includes(fileExtension)) {
            errors.push(`File type '${fileExtension}' is not allowed for security reasons`);
        }

        return {
            isValid: errors.length === 0,
            errors: errors,
            file: file, // CRITICAL FIX: Return the actual File object!
            fileInfo: {
                name: file.name,
                size: file.size,
                type: file.type,
                extension: fileExtension,
                sizeFormatted: this.formatFileSize(file.size)
            }
        };
    }

    /**
     * Validate multiple files
     * @param {File[]} files - Files to validate
     * @param {Object} [constraints={}] - Validation constraints
     * @returns {Object} - Validation result for all files
     */
    validateFiles(files, constraints = {}) {
        const results = files.map(file => this.validateFile(file, constraints));
        const validFiles = results.filter(result => result.isValid);
        const invalidFiles = results.filter(result => !result.isValid);

        return {
            isValid: invalidFiles.length === 0,
            validFiles: validFiles.map(result => result.file),
            invalidFiles: invalidFiles,
            totalSize: files.reduce((total, file) => total + file.size, 0),
            totalSizeFormatted: this.formatFileSize(files.reduce((total, file) => total + file.size, 0))
        };
    }

    /**
     * Format file size in human readable format
     * @param {number} bytes - File size in bytes
     * @returns {string} - Formatted file size
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Convenience method for GET requests
     * @param {string} endpoint - The API endpoint URL
     * @returns {Promise<Object>} - Parsed JSON data
     */
    async get(endpoint) {
        return this.apiCall(endpoint, 'GET');
    }

    /**
     * Convenience method for POST requests
     * @param {string} endpoint - The API endpoint URL
     * @param {Object} data - The request payload
     * @returns {Promise<Object>} - Parsed JSON data
     */
    async post(endpoint, data = null) {
        return this.apiCall(endpoint, 'POST', data);
    }

    /**
     * Convenience method for PUT requests
     * @param {string} endpoint - The API endpoint URL
     * @param {Object} data - The request payload
     * @returns {Promise<Object>} - Parsed JSON data
     */
    async put(endpoint, data = null) {
        return this.apiCall(endpoint, 'PUT', data);
    }

    /**
     * Convenience method for DELETE requests
     * @param {string} endpoint - The API endpoint URL
     * @returns {Promise<Object>} - Parsed JSON data
     */
    async delete(endpoint) {
        return this.apiCall(endpoint, 'DELETE');
    }
}

// Export a singleton instance of ApiService
const apiService = new ApiService();
export default apiService;